import { i18n } from '@lingui/core'
import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'

/**
 * Cómo se PINTA cada rol. La autoridad del rol es `identity.role` (catálogo
 * D-18, sembrado en la API); aquí solo viven las etiquetas visibles, que el
 * contrato no trae porque el front las decide.
 *
 * Solo los roles que hoy tienen pantalla. Un código sin fila se pinta tal
 * cual: mejor un `ROL-X-NN` visible que inventar un título.
 */
export interface RoleLabel {
  /** Siglas junto al nombre: `BD`. */
  short: string
  /** Nombre largo del rol: `Business Developer`. */
  title: string
}

/** El único rol que NO usa el shell del staff: su apartado es `/collaborator`. */
export const WORKER_ROLE = 'ROL-C-01'

/** Contabilidad no tiene tablero propio: su inicio es el Consolidado, nunca
    el Dashboard de Ventas al que caía por no estar en ninguna lista (`RoleHome`
    ya lo sabía; `DashboardPage` no — reproducido en vivo 2026-10-22). */
export const ACCOUNTING_ROLES = ['ROL-CO-01', 'ROL-CO-02']

/**
 * El título habla el idioma activo (D-36); las siglas no se traducen.
 *
 * Solo tenía 4 de los 19 roles del catálogo del vault (D-18, «Roles del
 * Sistema») — cualquier otro rol caía al `roleCode` crudo en el fallback
 * (`ROL-H-01` en vez de «Supervisor»), visible en la tarjeta de perfil del
 * sidebar. Completados los 15 que faltaban.
 */
const ROLE_MESSAGE: Partial<Record<string, { short: string; title: MessageDescriptor }>> = {
  'ROL-H-01': { short: 'SUP', title: msg`Supervisor` },
  'ROL-H-02': { short: 'MA', title: msg`Manager de Área` },
  'ROL-H-03': { short: 'MG', title: msg`Manager General` },
  'ROL-R-01': { short: 'RECL', title: msg`Reclutadora` },
  'ROL-R-02': { short: 'LIDER', title: msg`Líder de Grupo de Reclutadoras` },
  'ROL-R-03': { short: 'MREC', title: msg`Manager de Reclutamiento` },
  'ROL-V-01': { short: 'BD', title: msg`Business Developer` },
  'ROL-V-02': { short: 'BDC', title: msg`Business Developer Coordinator` },
  'ROL-I-01': { short: 'INSP', title: msg`Inspector` },
  'ROL-I-02': { short: 'COORD', title: msg`Coordinador de Inspección` },
  'ROL-Q-01': { short: 'OQA', title: msg`Operador de QA` },
  'ROL-Q-02': { short: 'MQA', title: msg`Manager de QA` },
  'ROL-CS-01': { short: 'AGCS', title: msg`Agente de Customer Service` },
  'ROL-CS-02': { short: 'CSM', title: msg`Customer Service Manager` },
  'ROL-CO-01': { short: 'CONT', title: msg`Contadora` },
  'ROL-CO-02': { short: 'MCO', title: msg`Manager de Contabilidad` },
  'ROL-C-01': { short: 'COLAB', title: msg`Colaborador` },
  'ROL-SYS-01': { short: 'SYS', title: msg`Sistema` },
  'ROL-ADM-01': { short: 'ADMIN', title: msg`Administrador` },
}

/** Se resuelve AL LLAMAR, no al cargar: así sigue al idioma en caliente. */
export function roleLabelOf(roleCode: string): RoleLabel {
  const entry = ROLE_MESSAGE[roleCode]
  return entry
    ? { short: entry.short, title: i18n._(entry.title) }
    : { short: roleCode, title: roleCode }
}

/**
 * Los tipos de usuario que el sistema da de alta, en el orden en que se
 * ofrecen (BDC, 2026-11-02): primero los tres del hotel —nacen en la
 * Conversión— y luego los internos del modal «Nuevo usuario». Fuera el
 * Sistema (no es una persona) y el Colaborador (nace en Reclutamiento).
 *
 * `name` es lo que SE GUARDA cuando un rol se usa como texto —el «Puesto» del
 * contacto del hotel—: el nombre en español, fijo en cualquier idioma, para
 * que un contacto capturado en inglés no quede en otro idioma que el resto.
 * Se pinta traducido con `jobTitleLabel`.
 */
export const CREATABLE_USER_ROLES: ReadonlyArray<{
  code: string
  name: string
  group: 'HOTEL' | 'ORANJE'
}> = [
  { code: 'ROL-H-03', name: 'Manager General', group: 'HOTEL' },
  { code: 'ROL-H-02', name: 'Manager de Área', group: 'HOTEL' },
  { code: 'ROL-H-01', name: 'Supervisor', group: 'HOTEL' },
  { code: 'ROL-V-01', name: 'Business Developer', group: 'ORANJE' },
  { code: 'ROL-V-02', name: 'Business Developer Coordinator', group: 'ORANJE' },
  { code: 'ROL-R-01', name: 'Reclutadora', group: 'ORANJE' },
  { code: 'ROL-R-02', name: 'Líder de Grupo de Reclutadoras', group: 'ORANJE' },
  { code: 'ROL-R-03', name: 'Manager de Reclutamiento', group: 'ORANJE' },
  { code: 'ROL-I-01', name: 'Inspector', group: 'ORANJE' },
  { code: 'ROL-I-02', name: 'Coordinador de Inspección', group: 'ORANJE' },
  { code: 'ROL-Q-01', name: 'Operador de QA', group: 'ORANJE' },
  { code: 'ROL-Q-02', name: 'Manager de QA', group: 'ORANJE' },
  { code: 'ROL-CS-01', name: 'Agente de Customer Service', group: 'ORANJE' },
  { code: 'ROL-CS-02', name: 'Customer Service Manager', group: 'ORANJE' },
  { code: 'ROL-CO-01', name: 'Contadora', group: 'ORANJE' },
  { code: 'ROL-CO-02', name: 'Manager de Contabilidad', group: 'ORANJE' },
  { code: 'ROL-ADM-01', name: 'Administrador', group: 'ORANJE' },
]

/**
 * Un puesto guardado, en el idioma activo si es uno de los roles del sistema.
 * Los puestos de antes de la lista (texto libre) se pintan tal cual.
 */
export function jobTitleLabel(jobTitle: string): string {
  const role = CREATABLE_USER_ROLES.find((item) => item.name === jobTitle)
  return role ? roleLabelOf(role.code).title : jobTitle
}
